import { DateTime } from "luxon";
import type { RecurringShiftRecurrenceTypeKeys } from "../components/RecurringShift/util/useRecurringShiftState";
import { MultiSizePopupState } from "../components/UIToolkit/Popups/MultiSizePopup/utils";
import { trackErrorEvent } from "../utils/analytics";
import {
	type DraggableFabOffset,
	type GapsViewFilterPreference,
	type LeakageReportFilters,
	MobileStaffingView,
	type PickupSuggestionState,
	RecurringShiftRecurrenceType,
	type StoredActivityLog,
	type StoredGroupedActivityLog,
	type StoredGroupedActivityLogSerialized,
	type StoredShiftWindow,
	UserView,
} from "../utils/models";
import type { SortingState } from "../utils/multiColumnSorting";

const popupClosedKeysPrefix = "popupClosed_";
const lastPopupShownDateKey = "lastPopupShownDate";
const feedbackPopoverClosedKey = "feedbackPopoverClosed";
const tutorialKeyPrefix = "tutorial-";
const oneTimeNoticeKeyPrefix = "oneTimeNotice-";
const chosenDepartmentIDKey = "chosenDepartmentID";
// The department whose look (logo + color) was last applied on this device.
// Per device, not per user, and kept on logout, like the theme keys it guards.
const departmentLookAppliedForKey = "department_look_applied_for";
// Which surface (admin/staff) the session is on. Stored because /refresh cannot recover it: the
// refresh token carries only user_id, and the access token is typically expired at that moment.
// It is a HINT only -- the backend re-derives the role from the database on every refresh, so a
// tampered value cannot grant admin access.
const chosenViewKey = "chosenView";
// Raw string, not JSON — impersonation writes it directly and AuthProvider reads it back as-is.
const originalTokenKey = "originalToken";
const savedCredentialsKey = "savedCredentials";
const biometricAvailableKey = "biometricAvailable";
const biometricTypeKey = "biometricType";
const declineBiometricKey = "declineBiometric";
const clientLastRefreshDataKeyV2 = "clientLastRefreshV2Data";
const recurringShiftSavedRecurrenceTypeKey = "recurringShiftSavedRecurrenceType";
const scheduleTemplateSelectedTabKey = "scheduleTemplateSelectedTab";
const consecutivePeriodKey = "consecutiveMasterSchedulePeriod";
const mobilePromotionBannerHideKey = "mobile-promotion-banner-hide";
const revealWageKey = "reveal_wage";
const activityLogDevModeKey = "activityLogDevMode";
const activityLogShowPunchesKey = "activityLogShowPunches";
const ctrlZTipLastTimeStampKey = "ctrlZTipLastTimeStamp";
const ctrlZTipUsedKey = "ctrlZTipUsed";
const swipeJiggleHintLastShownKey = "swipeJiggleHintLastShown";
const pickupSuggestionStateKey = "pickupSuggestionState";
const draftModeStateKey = "inhouse_draft_mode_state";
const leakageReportFiltersKey = "leakageReportFilters";
const weeklyGapsContractSectionsKey = "weeklyGapsContractSections";
const weeklyGapsDaySummaryCollapsedKey = "weeklyGapsDaySummaryCollapsed";
const dayViewStandardContractSectionsKey = "dayViewStandardContractSections";
const dayViewGapsContractSectionsKey = "dayViewGapsContractSections";
const mcOtModeKey = "mcOtMode";
const disciplinaryDateSortOrderKey = "disciplinaryDateSortOrder";
const draggableFabOffsetKeyPrefix = "draggableFabOffset_";
// Which mobile Staffing experience (gaps-first shell vs. classic list) a user chose, per department —
// the department id is appended, so switching facilities never carries the other one's choice over.
const mobileStaffingViewKeyPrefix = "mobileStaffingView_";
const gapsViewFilterKeyPrefix = "gapsViewFilter_";
// A Schedule Template filter, per user and department; the filter key names the tab and the filter.
const scheduleTemplateFilterKeyPrefix = "scheduleTemplateFilter_";
export const requestApprovalMultiSizePopupKey = "requestApprovalMultiSizePopup";
export const requestApprovalMultiSizePopupMedicalDepartmentKey = "requestApprovalMultiSizePopupMedicalDepartment";
export const addRecurringShiftMultiSizePopupKey = "addRecurringShiftMultiSizePopup";
const multiSizePopupValidKeys = [
	requestApprovalMultiSizePopupKey,
	requestApprovalMultiSizePopupMedicalDepartmentKey,
	addRecurringShiftMultiSizePopupKey,
	/**
	 * Used to defined storage keys for various multi-size popups - this helps us store the user's latest preference for popup size
	 * Add more keys here as needed
	 */
];
export const dailyViewStaffTableSortingKey = "dailyViewStaffTableSorting";
export const dailyViewGapsTableSortingKey = "dailyViewGapsTableSorting";
export const requestApprovalsMinimizedTableSortingKey = "requestApprovalsMinimizedTableSorting";
export const masterScheduleSortingKey = "masterScheduleSorting";
export const consecutiveMasterScheduleSortingKey = "consecutiveMasterScheduleSorting";
export const staffingPeopleSortingKey = "staffingPeopleSorting";
export const masterScheduleSectionOrderKey = "masterScheduleSectionOrder";
// Which sections are collapsed (hidden entirely)
const masterScheduleCollapsedSectionsKey = "masterScheduleCollapsedSections";
// Which sections show all locations vs only top 3 most popular
const masterScheduleExpandedLocationSectionsKey = "masterScheduleExpandedLocationSections";
const masterScheduleWeekStartDayKey = "masterScheduleWeekStartDay";
export const addNurseTableSortingKey = "addNurseTableSorting";
export const nurseRemovalTableSortingKey = "nurseRemovalTableSorting";
export const requestApprovalTableSortingKey = "requestApprovalTableSorting";
// Bumped to `…V2` when DAT-804 dropped the Position column: a persisted sort keeps the column's
// translated label, so an old entry would keep showing a "Position" chip in the sort popup and
// silently sort on a field that is no longer in the grid. A new key abandons that state.
export const hrisMappingCertSortingKey = "hrisMappingCertSortingV2";
export const hrisMappingEmploymentSortingKey = "hrisMappingEmploymentSorting";
export const hrisMappingEmployeeStatusSortingKey = "hrisMappingEmployeeStatusSorting";
export const hrisMappingTimeOffSortingKey = "hrisMappingTimeOffSorting";
export const hrisMappingPunchEventTypeIgnoreSortingKey = "hrisMappingPunchEventTypeIgnoreSorting";
export const hrisMappingEarningCodeIgnoreSortingKey = "hrisMappingEarningCodeIgnoreSorting";
export const hrisMappingSubRoleSortingKey = "hrisMappingSubRoleSorting";
export const featureFlagsAdminSortingKey = "featureFlagsAdminSorting";
export const disciplinaryReportTableSortingKey = "disciplinaryReportTableSorting";
// v2: the Status column's sort key changed from the dropped nurse-level `active` to
// `departmentEmployeeStatuses`. Restored sort state is matched to an option BY sortColumns, so a
// stored v1 entry would match nothing, keep no comparator, and silently sort on an undefined field
// while the chip still read "Status". Bumping the key discards those rather than honouring them.
export const teamTableSortingKey = "teamTableSorting-v2";
export const leakageReportTableSortingKey = "leakageReportTableSorting";
export const systemDataGridFixtureSortingKey = "systemDataGridFixtureSorting";
export const profileTimeOffTableSortingKey = "profile-time-off-table";
export const profileCallInsTableSortingKey = "profile-call-ins-table";
export const shiftPickupReportGroupedSortingKey = "shiftPickupReportGroupedSorting";
export const shiftPickupReportRawSortingKey = "shiftPickupReportRawSorting";
export const pbjShortShiftReviewSortingKey = "pbjShortShiftReviewSorting";
export const pbjLongShiftReviewSortingKey = "pbjLongShiftReviewSorting";
export const pbjSingleDayFlagSortingKey = "pbjSingleDayFlagSorting";
export const pbjDailyHoursCapSortingKey = "pbjDailyHoursCapSorting";
export const opsBackofficeVisibilityKey = "opsBackoffice.columnVisibility";
/**
 * Team > Users grid. `SystemDataGrid` derives both sorting and column visibility from one
 * `localStorageKey`, so this is in both allowlists below — hence the neutral name.
 */
export const teamAdminsGridKey = "teamAdmins.grid";
const opsBackofficeOpsOwnerFilterKey = "opsBackoffice.opsOwnerFilter";
/**
 * The MC multi-facility grid's `localStorageKey` — its sort, drag order, column widths and visibility all
 * persist under it. Versioned: a persisted visibility model wins over `defaultVisibility`, so a change to
 * the default column set needs a new version. Existing users' order, sort and widths are carried over from
 * the previous version once, on read (`migrateLegacyMultiFacilityGridStorage`); visibility deliberately is
 * not, so everyone lands on the current default set.
 */
export const managementCenterMultiGridKey = "managementCenterMulti.grid.v3";
/** The grid's previous key. Read only by that one-time migration, which removes each entry it reads. */
export const legacyManagementCenterMultiGridKey = "managementCenterMulti.columnVisibility.v2";
export const hiringHubCandidatesGridKey = "hiringHubCandidatesGrid";
/** The Careers Pipeline → Table grid: sort, column order, widths and visibility, per user, under one key. */
export const careersCandidatesGridKey = "careersCandidatesGrid";
/** The Careers Jobs grid: sort, column order, widths and visibility, per user, under one key. */
export const careersJobsGridKey = "careersJobsGrid";
export const activityLogViewModeKey = "activityLogViewMode";
/** The Grouped Activity Log grid's sort. Restored entries are matched to an option by `sortColumns`, so
 * a change to a sort axis's columns needs a new version — the same rule as `teamTableSortingKey`. */
export const activityLogGroupedSortingKey = "activityLogGroupedSorting";
const columnVisibilityValidKeys = [opsBackofficeVisibilityKey, managementCenterMultiGridKey, legacyManagementCenterMultiGridKey, teamAdminsGridKey];

const tableSortingValidKeys = [
	dailyViewStaffTableSortingKey,
	dailyViewGapsTableSortingKey,
	requestApprovalsMinimizedTableSortingKey,
	masterScheduleSortingKey,
	addNurseTableSortingKey,
	nurseRemovalTableSortingKey,
	requestApprovalTableSortingKey,
	hrisMappingCertSortingKey,
	hrisMappingEmploymentSortingKey,
	hrisMappingEmployeeStatusSortingKey,
	hrisMappingTimeOffSortingKey,
	hrisMappingPunchEventTypeIgnoreSortingKey,
	hrisMappingEarningCodeIgnoreSortingKey,
	hrisMappingSubRoleSortingKey,
	featureFlagsAdminSortingKey,
	disciplinaryReportTableSortingKey,
	teamTableSortingKey,
	teamAdminsGridKey,
	leakageReportTableSortingKey,
	systemDataGridFixtureSortingKey,
	profileTimeOffTableSortingKey,
	profileCallInsTableSortingKey,
	shiftPickupReportGroupedSortingKey,
	shiftPickupReportRawSortingKey,
	pbjShortShiftReviewSortingKey,
	pbjLongShiftReviewSortingKey,
	pbjSingleDayFlagSortingKey,
	pbjDailyHoursCapSortingKey,
	// Shared with the column-visibility allowlist: the MC multi grid persists sort, order, widths and
	// visibility under one key. The legacy key stays allowlisted so its one-time migration can read it.
	managementCenterMultiGridKey,
	legacyManagementCenterMultiGridKey,
	// Hiring Hub candidates grid — persists sort, order, visibility, AND column widths under one key.
	hiringHubCandidatesGridKey,
	// Careers' two grids — each persists sort, order, visibility AND column widths under its one key.
	careersCandidatesGridKey,
	careersJobsGridKey,
	activityLogGroupedSortingKey,
	/**
	 * Used to defined storage keys for various table sorting states - this helps us store the user's latest preference for table sorting
	 * Add more keys here as needed
	 */
];

function _userIdToUserStorageKey(userId: string, key: string) {
	return `selfStorage.${userId}.${key}`;
}

function _setItemPerUserId<T>(userId: string, key: string, value: T, avoidLegacyWrite = false): void {
	try {
		const storageKey = _userIdToUserStorageKey(userId, key);
		const serializedValue = JSON.stringify(value);
		localStorage.setItem(storageKey, serializedValue);

		if (!avoidLegacyWrite) {
			// backwards support - write to old key without userid
			localStorage.setItem(key, serializedValue);
		}
	} catch (error) {
		console.error(`Failed to set item per user ${userId} with key ${key} in localStorage:`, error);
	}
}

function _getItemPerUserId<T>(userId: string, key: string): T | null {
	try {
		const storageKey = _userIdToUserStorageKey(userId, key);
		const result = _getItem<T>(storageKey);
		if (result === undefined || result === null) {
			// backwards compatability - use old key without user id
			return _getItem(key);
		}
		return result;
	} catch (error) {
		console.error(`Failed to get item per user ${userId} with key ${key} from localStorage:`, error);
		return null;
	}
}

function _removeItemPerUserId(userId: string, key: string) {
	try {
		const storageKey = _userIdToUserStorageKey(userId, key);
		_removeItem(storageKey);

		// backwards support - remove old key without userid
		_removeItem(key);
	} catch (error) {
		console.error(`Failed to remove item per user ${userId} with key ${key} from localStorage:`, error);
	}
}

/**
 * @see _setItemPerUserId - we should not use `_setItem` directly for per-user data, only use it if you have a perfect reason for that.
 */
function _setItem<T>(key: string, value: T): void {
	try {
		const serializedValue = JSON.stringify(value);
		localStorage.setItem(key, serializedValue);
	} catch (error) {
		console.error(`Failed to set ${key} in localStorage:`, error);
	}
}

/**
 * @see _getItemPerUserId - we should not use `_getItem` directly for per-user data, only use it if you have a perfect reason for that.
 */
function _getItem<T>(key: string): T | null {
	try {
		const item = localStorage.getItem(key);
		return item ? JSON.parse(item) : null;
	} catch (error) {
		console.error(`Failed to parse ${key} from localStorage:`, error);
		return null;
	}
}

/**
 * @see _removeItemPerUserId - we should not use `_removeItem` directly for per-user data, only use it if you have a perfect reason for that.
 */
function _removeItem(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch (error) {
		console.error(`Failed to remove ${key} from localStorage:`, error);
	}
}

function hasItem(key: string): boolean {
	try {
		return localStorage.getItem(key) !== null;
	} catch (error) {
		console.error(`Failed to read ${key} from localStorage:`, error);
		return false;
	}
}

function clear(): void {
	try {
		localStorage.clear();
	} catch (error) {
		console.error("Failed to clear localStorage:", error);
	}
}

function setChosenDepartmentID(departmentID: string): void {
	_setItem(chosenDepartmentIDKey, departmentID);
}
function getChosenDepartmentID(): string | null {
	return _getItem<string>(chosenDepartmentIDKey);
}
function removeChosenDepartmentID(): void {
	_removeItem(chosenDepartmentIDKey);
}

function setDepartmentLookAppliedFor(departmentID: string): void {
	_setItem(departmentLookAppliedForKey, departmentID);
}
function getDepartmentLookAppliedFor(): string | null {
	const stored = _getItem<unknown>(departmentLookAppliedForKey);
	return typeof stored === "string" ? stored : null;
}
function removeDepartmentLookAppliedFor(): void {
	_removeItem(departmentLookAppliedForKey);
}

function setChosenView(view: string): void {
	_setItem(chosenViewKey, view);
}
function getChosenView(): UserView | null {
	// Validated on read: the value is sent to /refresh, and a stale or tampered one ("manager")
	// fails validation there instead of falling back to the role-implied view.
	const stored = _getItem<string>(chosenViewKey);
	return stored === UserView.admin || stored === UserView.staff ? stored : null;
}
function removeChosenView(): void {
	_removeItem(chosenViewKey);
}

// The token to return to when impersonation stops. Kept only while one is in progress, so the view
// switch can't overwrite it and a leftover from an old session isn't adopted by the next user.
function storeOriginalTokenOnce(currentToken: string | undefined, alreadyImpersonating: boolean): boolean {
	if (!currentToken || (alreadyImpersonating && localStorage.getItem(originalTokenKey))) return false;
	localStorage.setItem(originalTokenKey, currentToken);
	return true;
}
function getOriginalToken(): string | null {
	return localStorage.getItem(originalTokenKey);
}

function setClientGroupedRefreshData(data: StoredGroupedActivityLog, userId: string) {
	const serializedData: StoredGroupedActivityLogSerialized = {
		notifications: {
			lastChange: data.notifications.lastChange.toISO() as string,
		},
		requests: {
			lastChange: data.requests.lastChange.toISO() as string,
		},
		nurses: {
			lastChange: data.nurses.lastChange.toISO() as string,
		},
		departmentData: {
			lastChange: data.departmentData.lastChange.toISO() as string,
		},
		wage: {
			lastChange: data.wage.lastChange.toISO() as string,
		},
	};
	_setItemPerUserId(userId, clientLastRefreshDataKeyV2, serializedData);
}
function removeClientGroupedRefreshData(userId: string) {
	_removeItemPerUserId(userId, clientLastRefreshDataKeyV2);
}
function getClientGroupedRefreshData(userId: string): StoredGroupedActivityLog | null {
	const serializedData = _getItemPerUserId<StoredGroupedActivityLogSerialized>(userId, clientLastRefreshDataKeyV2);
	if (!serializedData) return null;

	return {
		notifications: {
			lastChange: DateTime.fromISO(serializedData.notifications.lastChange),
		},
		requests: {
			lastChange: DateTime.fromISO(serializedData.requests.lastChange),
		},
		nurses: {
			lastChange: DateTime.fromISO(serializedData.nurses.lastChange),
		},
		departmentData: {
			lastChange: DateTime.fromISO(serializedData.departmentData.lastChange),
		},
		// Pre-existing storage may not include wage; fall back to "now" so the next poll has a valid cutoff.
		wage: {
			lastChange: serializedData.wage?.lastChange ? DateTime.fromISO(serializedData.wage.lastChange) : DateTime.now(),
		},
	};
}

function resetClientGroupedRefreshData(userId: string) {
	const timeStamp = DateTime.now();
	const emptyResult: StoredActivityLog = { lastChange: timeStamp };
	const groupedResult = {
		requests: emptyResult,
		nurses: emptyResult,
		departmentData: emptyResult,
		notifications: emptyResult,
		wage: emptyResult,
	} as StoredGroupedActivityLog;
	setClientGroupedRefreshData(groupedResult, userId);
	return groupedResult;
}

function setSavedCredentials(value: boolean): void {
	_setItem(savedCredentialsKey, value);
}
function getSavedCredentials(): boolean {
	return !!_getItem<boolean>(savedCredentialsKey);
}
function removeSavedCredentials(): void {
	_removeItem(savedCredentialsKey);
}

function setBiometricAvailable(value: boolean): void {
	_setItem(biometricAvailableKey, value);
}
function getBiometricAvailable(): boolean {
	return !!_getItem<boolean>(biometricAvailableKey);
}
function removeBiometricAvailable(): void {
	_removeItem(biometricAvailableKey);
}

function setBiometricType(value: string): void {
	_setItem(biometricTypeKey, value);
}
function getBiometricType(): string | null {
	return _getItem<string>(biometricTypeKey);
}
function removeBiometricType(): void {
	_removeItem(biometricTypeKey);
}

function setDeclineBiometric(value: boolean): void {
	_setItem(declineBiometricKey, value);
}
function getDeclineBiometric(): boolean {
	return !!_getItem<boolean>(declineBiometricKey);
}
function removeDeclineBiometric(): void {
	_removeItem(declineBiometricKey);
}
function setRecurringShiftSavedRecurrenceType(value: RecurringShiftRecurrenceTypeKeys, userId: string): void {
	_setItemPerUserId(userId, recurringShiftSavedRecurrenceTypeKey, value);
}

function isValidRecurringShiftType(x: string | null): x is RecurringShiftRecurrenceType {
	return !!x && Object.keys(RecurringShiftRecurrenceType).includes(x as RecurringShiftRecurrenceType);
}
function getRecurringShiftSavedRecurrenceType(userId: string): RecurringShiftRecurrenceTypeKeys | null {
	const result = _getItemPerUserId<RecurringShiftRecurrenceTypeKeys>(userId, recurringShiftSavedRecurrenceTypeKey);
	return isValidRecurringShiftType(result) ? result : null;
}
function removeRecurringShiftSavedRecurrenceType(userId: string): void {
	_removeItemPerUserId(userId, recurringShiftSavedRecurrenceTypeKey);
}

function setScheduleTemplateTab(value: string, userId: string): void {
	_setItemPerUserId(userId, scheduleTemplateSelectedTabKey, value);
}
function getScheduleTemplateTab(userId: string): string | null {
	return _getItemPerUserId<string>(userId, scheduleTemplateSelectedTabKey);
}

type StoredConsecutivePeriod = { workingDays: number; restDays: number };
function setConsecutivePeriod(value: StoredConsecutivePeriod, userId: string): void {
	_setItemPerUserId(userId, consecutivePeriodKey, value);
}
function getConsecutivePeriod(userId: string): StoredConsecutivePeriod | null {
	const stored = _getItemPerUserId<StoredConsecutivePeriod>(userId, consecutivePeriodKey);
	if (!stored || typeof stored.workingDays !== "number" || typeof stored.restDays !== "number" || stored.workingDays < 1 || stored.restDays < 1) {
		return null;
	}
	return stored;
}

function saveTableSortingState<T>(key: string, sortState: SortingState<T>[], userId: string): void {
	if (tableSortingValidKeys.includes(key)) {
		_setItemPerUserId(userId, key, sortState);
	} else {
		console.warn(`Attempted to save table sorting state with invalid key that is not included in tableSortingValidKeys: ${key}`);
	}
}
function getTableSortingState<T>(key: string, userId: string): SortingState<T>[] | null {
	if (tableSortingValidKeys.includes(key)) {
		return _getItemPerUserId<SortingState<T>[]>(userId, key);
	}
	console.warn(`Attempted to get table sorting state with invalid key that is not included in tableSortingValidKeys: ${key}`);
	return null;
}
/**
 * The sort saved for THIS user only. `getTableSortingState` falls back to the raw key, which every user on
 * the browser shares (the sort is also written there for backwards compatibility) — so it cannot tell
 * "this user has a saved sort" from "someone on this browser has one". Use this when that distinction
 * matters, e.g. deciding whether a one-time migration already ran for this user.
 */
function getOwnTableSortingState<T>(key: string, userId: string): SortingState<T>[] | null {
	if (tableSortingValidKeys.includes(key)) {
		return _getItem<SortingState<T>[]>(_userIdToUserStorageKey(userId, key));
	}
	console.warn(`Attempted to get table sorting state with invalid key that is not included in tableSortingValidKeys: ${key}`);
	return null;
}
// Clearing the persisted sort makes a fresh mount fall back to the grid's `defaultSortState` — used when
// a saved view carries no sort, so applying it restores the default order (e.g. the ops stage grouping,
// whose custom comparator can't survive a JSON round-trip) instead of an empty/alphabetical sort.
function removeTableSortingState(key: string, userId: string): void {
	if (tableSortingValidKeys.includes(key)) {
		_removeItemPerUserId(userId, key);
	} else {
		console.warn(`Attempted to remove table sorting state with invalid key that is not included in tableSortingValidKeys: ${key}`);
	}
}

// Column order is persisted as an ordered list of column `field` names, scoped under the table's
// key + this suffix so it shares one allowlist entry per table without colliding with the stored
// sort state. A table opts in by passing a `localStorageKey` already allowlisted for either sorting
// or column visibility — the two existing per-table preference allowlists.
const tableColumnOrderKeySuffix = "-columnOrder";
function isColumnOrderKeyAllowed(key: string): boolean {
	return tableSortingValidKeys.includes(key) || columnVisibilityValidKeys.includes(key);
}
function saveTableColumnOrder(key: string, fieldOrder: string[], userId: string): void {
	if (isColumnOrderKeyAllowed(key)) {
		_setItemPerUserId(userId, `${key}${tableColumnOrderKeySuffix}`, fieldOrder, true);
	} else {
		console.warn(`Attempted to save table column order with a key not allowlisted for sorting or column visibility: ${key}`);
	}
}
function getTableColumnOrder(key: string, userId: string): string[] | null {
	if (isColumnOrderKeyAllowed(key)) {
		return _getItemPerUserId<string[]>(userId, `${key}${tableColumnOrderKeySuffix}`);
	}
	console.warn(`Attempted to get table column order with a key not allowlisted for sorting or column visibility: ${key}`);
	return null;
}
function removeTableColumnOrder(key: string, userId: string): void {
	if (isColumnOrderKeyAllowed(key)) {
		_removeItemPerUserId(userId, `${key}${tableColumnOrderKeySuffix}`);
	}
}

// Column visibility is persisted as a `{ field: false }` model (MUI's GridColumnVisibilityModel),
// scoped under the table's key + this suffix — the same shape as column order, so one allowlist entry
// per table covers sorting, order, AND visibility without any of the three colliding at the raw key.
// (The raw key already holds sort state via `saveTableSortingState`; storing visibility there too would
// clobber it — hence the distinct suffix.) A table opts in with a `localStorageKey` allowlisted for
// either sorting or column visibility.
const tableColumnVisibilityKeySuffix = "-columnVisibility";
/**
 * Whether the table's RAW key may hold a pre-suffix visibility model (the old page-scoped Ops Backoffice
 * picker). Not for a table that is also sort-allowlisted: its raw key holds the persisted SORT, which the
 * legacy fallback would otherwise read as a visibility model — moving the sort into the visibility slot
 * and deleting it — and a visibility reset would otherwise wipe it.
 */
function hasLegacyRawVisibilitySlot(key: string): boolean {
	return columnVisibilityValidKeys.includes(key) && !tableSortingValidKeys.includes(key);
}
function isColumnVisibilityKeyAllowed(key: string): boolean {
	return tableSortingValidKeys.includes(key) || columnVisibilityValidKeys.includes(key);
}
function saveTableColumnVisibility(key: string, model: Record<string, boolean>, userId: string): void {
	if (isColumnVisibilityKeyAllowed(key)) {
		_setItemPerUserId(userId, `${key}${tableColumnVisibilityKeySuffix}`, model, true);
	} else {
		console.warn(`Attempted to save table column visibility with a key not allowlisted for sorting or column visibility: ${key}`);
	}
}
function getTableColumnVisibility(key: string, userId: string): Record<string, boolean> | null {
	if (!isColumnVisibilityKeyAllowed(key)) {
		console.warn(`Attempted to get table column visibility with a key not allowlisted for sorting or column visibility: ${key}`);
		return null;
	}
	const scopedKey = `${key}${tableColumnVisibilityKeySuffix}`;
	const stored = _getItemPerUserId<Record<string, boolean>>(userId, scopedKey);
	if (stored != null) return stored;
	// Legacy migration: the page-scoped Ops Backoffice picker stored visibility at the raw key (no user
	// scoping, no suffix) before this became a shared grid feature. That raw key is global to the device,
	// so reading it directly would let one user's saved layout resurface in another user's session. Migrate
	// it once into this user's scoped key and delete the raw entry, so the layout survives the move without
	// leaking across users (whoever loads first claims it; later sessions no longer see it).
	if (hasLegacyRawVisibilitySlot(key)) {
		const legacy = _getItemPerUserId<Record<string, boolean>>(userId, key);
		if (legacy != null) {
			_setItemPerUserId(userId, scopedKey, legacy, true);
			_removeItemPerUserId(userId, key);
			return legacy;
		}
	}
	return null;
}
function removeTableColumnVisibility(key: string, userId: string): void {
	if (!isColumnVisibilityKeyAllowed(key)) return;
	_removeItemPerUserId(userId, `${key}${tableColumnVisibilityKeySuffix}`);
	// Also clear the pre-suffix legacy entry for allowlisted tables — otherwise getTableColumnVisibility
	// would fall back to (and re-migrate) the old raw-key layout, undoing the reset the user just made.
	if (hasLegacyRawVisibilitySlot(key)) {
		_removeItemPerUserId(userId, key);
	}
}
// Column widths are persisted as a `{ field: pixelWidth }` map for the columns the user has manually
// resized, scoped under the table's key + this suffix (distinct from sort/order/visibility so none collide
// at the raw key). Same allowlist gate as order/visibility: a table opts in with an allowlisted key.
const tableColumnWidthsKeySuffix = "-columnWidths";
function saveTableColumnWidths(key: string, widths: Record<string, number>, userId: string): void {
	if (isColumnVisibilityKeyAllowed(key)) {
		_setItemPerUserId(userId, `${key}${tableColumnWidthsKeySuffix}`, widths, true);
	} else {
		console.warn(`Attempted to save table column widths with a key not allowlisted for sorting or column visibility: ${key}`);
	}
}
function getTableColumnWidths(key: string, userId: string): Record<string, number> | null {
	if (isColumnVisibilityKeyAllowed(key)) {
		return _getItemPerUserId<Record<string, number>>(userId, `${key}${tableColumnWidthsKeySuffix}`);
	}
	console.warn(`Attempted to get table column widths with a key not allowlisted for sorting or column visibility: ${key}`);
	return null;
}
function removeTableColumnWidths(key: string, userId: string): void {
	if (isColumnVisibilityKeyAllowed(key)) {
		_removeItemPerUserId(userId, `${key}${tableColumnWidthsKeySuffix}`);
	}
}
function setOpsOwnerFilter(userId: string, ownerIds: string[]): void {
	_setItemPerUserId(userId, opsBackofficeOpsOwnerFilterKey, ownerIds, true);
}
function getOpsOwnerFilter(userId: string): string[] | null {
	return _getItemPerUserId<string[]>(userId, opsBackofficeOpsOwnerFilterKey);
}
function setMultiSizePopupState(popupKey: string, value: MultiSizePopupState, userId: string): void {
	if (multiSizePopupValidKeys.includes(popupKey)) {
		_setItemPerUserId(userId, popupKey, value);
		return;
	}
	console.warn(`Attempted to set multiSize popup state with invalid key: ${popupKey}`);
	trackErrorEvent("set_multi_size_popup_key_invalid_multiSize_popup_key", {
		popupKey,
	});
}
function getMultiSizePopupState(popupKey: string, userId: string): MultiSizePopupState {
	if (multiSizePopupValidKeys.includes(popupKey)) {
		const value = _getItemPerUserId<MultiSizePopupState>(userId, popupKey);
		return value !== null ? value : MultiSizePopupState.MINIMIZED;
	}
	console.warn(`Attempted to get multiSize popup state with invalid key: ${popupKey}`);
	trackErrorEvent("get_multi_size_popup_key_invalid_multiSize_popup_key", {
		popupKey,
	});
	return MultiSizePopupState.HIDDEN;
}

function getMobilePromotionBannerHide(userId: string): boolean {
	return !!_getItemPerUserId<boolean>(userId, mobilePromotionBannerHideKey);
}

function setMobilePromotionBannerHide(value: boolean, userId: string): void {
	_setItemPerUserId(userId, mobilePromotionBannerHideKey, value);
}

function removeMobilePromotionBannerHide(userId: string): void {
	_removeItemPerUserId(userId, mobilePromotionBannerHideKey);
}

function getRevealWage(userId: string, hideWageFromPermissions: boolean): boolean {
	return _getItemPerUserId<boolean>(userId, revealWageKey) ?? !hideWageFromPermissions;
}

function setRevealWage(value: boolean, userId: string): void {
	_setItemPerUserId(userId, revealWageKey, value);
}

function removeRevealWage(userId: string): void {
	_removeItemPerUserId(userId, revealWageKey);
}

/** Default OFF when unset — same "advanced/internal view, opt in per user" convention as `getRevealWage`. */
function getActivityLogDevMode(userId: string): boolean {
	return !!_getItemPerUserId<boolean>(userId, activityLogDevModeKey);
}

function setActivityLogDevMode(value: boolean, userId: string): void {
	_setItemPerUserId(userId, activityLogDevModeKey, value);
}

/** Default OFF when unset — punch (`nurse_in_shift_actual`) rows are excluded from the grouped activity
 * log by default, both server-side and in this persisted preference. */
function getActivityLogShowPunches(userId: string): boolean {
	return !!_getItemPerUserId<boolean>(userId, activityLogShowPunchesKey);
}

function setActivityLogShowPunches(value: boolean, userId: string): void {
	_setItemPerUserId(userId, activityLogShowPunchesKey, value);
}

function getLastCtrlZTipTimestamp(userId: string): DateTime | null {
	const stamp = _getItemPerUserId<string>(userId, ctrlZTipLastTimeStampKey);
	const asLuxon = stamp ? DateTime.fromISO(stamp) : null;
	return asLuxon?.isValid ? asLuxon : null;
}

function setLastCtrlZTipTimestamp(timestamp: DateTime, userId: string): void {
	_setItemPerUserId(userId, ctrlZTipLastTimeStampKey, timestamp.toISO());
}

function getCtrlZTipUsed(userId: string): boolean {
	const value = _getItemPerUserId<boolean>(userId, ctrlZTipUsedKey);
	return value !== null ? value : false;
}
function setCtrlZTipUsed(value: boolean, userId: string): void {
	_setItemPerUserId(userId, ctrlZTipUsedKey, value);
}
function resetSwipeJiggleHintLastShown(userId: string): void {
	_setItemPerUserId(userId, swipeJiggleHintLastShownKey, DateTime.now().toISO());
}
function removeSwipeJiggleHintLastShown(userId: string): void {
	_removeItemPerUserId(userId, swipeJiggleHintLastShownKey);
}
function getSwipeJiggleHintLastShown(userId: string): DateTime | null {
	const stamp = _getItemPerUserId<string>(userId, swipeJiggleHintLastShownKey);
	const asLuxon = stamp ? DateTime.fromISO(stamp) : null;
	return asLuxon?.isValid ? asLuxon : null;
}

function getFeedbackPopoverClosed(userId: string): boolean {
	return !!_getItemPerUserId<boolean>(userId, feedbackPopoverClosedKey);
}

function setFeedbackPopoverClosed(value: boolean, userId: string): void {
	_setItemPerUserId(userId, feedbackPopoverClosedKey, value);
}

function getTutorialDismissed(tutorialKey: string, userId: string): boolean {
	const key = `${tutorialKeyPrefix}${tutorialKey}`;
	return !!_getItemPerUserId<boolean>(userId, key);
}

function setTutorialDismissed(tutorialKey: string, value: boolean, userId: string): void {
	_setItemPerUserId(userId, `${tutorialKeyPrefix}${tutorialKey}`, value);
}

/**
 * Per-user acknowledgement of a one-time notice (see `UIToolkit/molecules/OneTimeNotice`). Unset =
 * never acknowledged; the flag is only ever written, so a notice cannot come back after "got it".
 */
function getOneTimeNoticeDismissed(noticeKey: string, userId: string): boolean {
	return !!_getItemPerUserId<boolean>(userId, `${oneTimeNoticeKeyPrefix}${noticeKey}`);
}

function setOneTimeNoticeDismissed(noticeKey: string, userId: string): void {
	// `avoidLegacyWrite` — these keys are new, so there is no un-scoped value to stay compatible with.
	// Writing one would be actively wrong: `_getItemPerUserId` falls back to the un-scoped key, so the
	// next user signing in on this browser would inherit someone else's acknowledgement.
	_setItemPerUserId(userId, `${oneTimeNoticeKeyPrefix}${noticeKey}`, true, true);
}

function getPopupClosed(localStorageKey: string, userId: string): boolean {
	const key = `${popupClosedKeysPrefix}${localStorageKey}`;
	return !!_getItemPerUserId<boolean>(userId, key);
}

function setPopupClosed(localStorageKey: string, value: boolean, userId: string): void {
	_setItemPerUserId(userId, `${popupClosedKeysPrefix}${localStorageKey}`, value);
}

function getLastPopupShownDate(userId: string): string | null {
	return _getItemPerUserId<string>(userId, lastPopupShownDateKey);
}

function setLastPopupShownDate(date: string, userId: string): void {
	_setItemPerUserId(userId, lastPopupShownDateKey, date);
}

function getDraftModeState<T>(userId: string): T | null {
	return _getItemPerUserId<T>(userId, draftModeStateKey);
}

function setDraftModeState<T>(value: T, userId: string): void {
	_setItemPerUserId(userId, draftModeStateKey, value);
}

function removeDraftModeState(userId: string): void {
	_removeItemPerUserId(userId, draftModeStateKey);
}

function getSectionOrder(departmentId: string, userId: string): string[] | null {
	const key = `${masterScheduleSectionOrderKey}_${departmentId}`;
	return _getItemPerUserId<string[]>(userId, key);
}

function setSectionOrder(departmentId: string, order: string[], userId: string): void {
	const key = `${masterScheduleSectionOrderKey}_${departmentId}`;
	_setItemPerUserId(userId, key, order);
}

function removeSectionOrder(departmentId: string, userId: string): void {
	_removeItemPerUserId(userId, `${masterScheduleSectionOrderKey}_${departmentId}`);
}

function getCollapsedSections(departmentId: string, userId: string): string[] | null {
	const key = `${masterScheduleCollapsedSectionsKey}_${departmentId}`;
	return _getItemPerUserId<string[]>(userId, key);
}

function setCollapsedSections(departmentId: string, sections: string[], userId: string): void {
	const key = `${masterScheduleCollapsedSectionsKey}_${departmentId}`;
	_setItemPerUserId(userId, key, sections);
}

function getExpandedLocationSections(departmentId: string, userId: string): string[] | null {
	const key = `${masterScheduleExpandedLocationSectionsKey}_${departmentId}`;
	return _getItemPerUserId<string[]>(userId, key);
}

function setExpandedLocationSections(departmentId: string, sections: string[], userId: string): void {
	const key = `${masterScheduleExpandedLocationSectionsKey}_${departmentId}`;
	_setItemPerUserId(userId, key, sections);
}

function getViewWeekStartDay(departmentId: string, userId: string): number | null {
	const key = `${masterScheduleWeekStartDayKey}_${departmentId}`;
	return _getItemPerUserId<number>(userId, key);
}

function setViewWeekStartDay(departmentId: string, weekStartDay: number, userId: string): void {
	const key = `${masterScheduleWeekStartDayKey}_${departmentId}`;
	_setItemPerUserId(userId, key, weekStartDay);
}

function getLeakageReportFilters(departmentId: string, userId: string): LeakageReportFilters | null {
	const key = `${leakageReportFiltersKey}_${departmentId}`;
	return _getItemPerUserId<LeakageReportFilters>(userId, key);
}

function setLeakageReportFilters(departmentId: string, filters: LeakageReportFilters, userId: string): void {
	const key = `${leakageReportFiltersKey}_${departmentId}`;
	_setItemPerUserId(userId, key, filters);
}

function getWeeklyGapsContractSections(departmentId: string, userId: string): string[] | null {
	const key = `${weeklyGapsContractSectionsKey}_${departmentId}`;
	return _getItemPerUserId<string[]>(userId, key);
}

function setWeeklyGapsContractSections(departmentId: string, sections: string[], userId: string): void {
	const key = `${weeklyGapsContractSectionsKey}_${departmentId}`;
	_setItemPerUserId(userId, key, sections);
}

function getDayViewStandardContractSections(departmentId: string, userId: string): string[] | null {
	const key = `${dayViewStandardContractSectionsKey}_${departmentId}`;
	return _getItemPerUserId<string[]>(userId, key);
}

function setDayViewStandardContractSections(departmentId: string, sections: string[], userId: string): void {
	const key = `${dayViewStandardContractSectionsKey}_${departmentId}`;
	_setItemPerUserId(userId, key, sections);
}

function getDayViewGapsContractSections(departmentId: string, userId: string): string[] | null {
	const key = `${dayViewGapsContractSectionsKey}_${departmentId}`;
	return _getItemPerUserId<string[]>(userId, key);
}

function setDayViewGapsContractSections(departmentId: string, sections: string[], userId: string): void {
	const key = `${dayViewGapsContractSectionsKey}_${departmentId}`;
	_setItemPerUserId(userId, key, sections);
}

function getMcOtMode(departmentId: string, userId: string): "trigger" | "actual" | null {
	const key = `${mcOtModeKey}_${departmentId}`;
	return _getItemPerUserId<"trigger" | "actual">(userId, key);
}

function setMcOtMode(departmentId: string, mode: "trigger" | "actual", userId: string): void {
	const key = `${mcOtModeKey}_${departmentId}`;
	_setItemPerUserId(userId, key, mode);
}

function getPickupSuggestionState(userId: string): PickupSuggestionState | null {
	return _getItemPerUserId<PickupSuggestionState>(userId, pickupSuggestionStateKey);
}

function setPickupSuggestionState(state: PickupSuggestionState, userId: string): void {
	// should avoid legacy write to not leak pickup suggestion state across users, we rather get null for other users than wrong state
	_setItemPerUserId(userId, pickupSuggestionStateKey, state, true);
}

function getDisciplinaryDateSortOrder(userId: string): "asc" | "desc" | null {
	return _getItemPerUserId<"asc" | "desc">(userId, disciplinaryDateSortOrderKey);
}

function setDisciplinaryDateSortOrder(value: "asc" | "desc", userId: string): void {
	_setItemPerUserId(userId, disciplinaryDateSortOrderKey, value);
}

function getWeeklyGapsDaySummaryCollapsed(userId: string): boolean {
	const value = _getItemPerUserId<boolean>(userId, weeklyGapsDaySummaryCollapsedKey);
	return typeof value === "boolean" ? value : false;
}

function setWeeklyGapsDaySummaryCollapsed(value: boolean, userId: string): void {
	_setItemPerUserId(userId, weeklyGapsDaySummaryCollapsedKey, value);
}

function getDraggableFabOffset(storageKey: string, userId: string): DraggableFabOffset | null {
	return _getItemPerUserId<DraggableFabOffset>(userId, `${draggableFabOffsetKeyPrefix}${storageKey}`);
}

function setDraggableFabOffset(storageKey: string, offset: DraggableFabOffset, userId: string): void {
	// No legacy (user-less) write: where one user parked a floating button is not another user's preference.
	_setItemPerUserId(userId, `${draggableFabOffsetKeyPrefix}${storageKey}`, offset, true);
}

/**
 * The mobile Staffing view this user picked for this department, or `null` when they never picked one.
 *
 * Deliberately null rather than a default: "never chose" and "chose classic" are different states, and
 * only the caller knows which view a first visit should land on (it depends on the rollout flag and the
 * department's type). Anything stored that is not a known view is treated as unset — a hand-edited or
 * stale value must not decide which screen a head nurse gets.
 */
function getMobileStaffingView(departmentId: string, userId: string): MobileStaffingView | null {
	const stored = _getItemPerUserId<string>(userId, `${mobileStaffingViewKeyPrefix}${departmentId}`);
	return stored === MobileStaffingView.Gaps || stored === MobileStaffingView.Classic ? stored : null;
}

function setMobileStaffingView(departmentId: string, view: MobileStaffingView, userId: string): void {
	// No legacy (user-less) write: the legacy key is shared by every user on the browser, so without this
	// a shift lead opening the app on the unit's tablet would inherit whichever view the last one chose.
	_setItemPerUserId(userId, `${mobileStaffingViewKeyPrefix}${departmentId}`, view, true);
}

/**
 * The gaps-first home's saved scope for this user and department — the jobs, units and shift windows
 * they last chose to see, or `null` when they never saved one.
 *
 * Stored as names rather than ids because that is what the screen filters on: the sections and time
 * windows the gap view produces are labels, and a department's locations are keyed by name throughout
 * `useWeeklyGapView`. A row stored as `null` was never narrowed and follows whatever the department has.
 */
function getGapsViewFilter(departmentId: string, userId: string): GapsViewFilterPreference | null {
	const key = `${gapsViewFilterKeyPrefix}${departmentId}`;
	const stored = _getItemPerUserId<GapsViewFilterPreference>(userId, key);
	if (!stored || typeof stored !== "object") return null;
	const { jobLabels, locationNames, timeLabels } = stored;
	if (!isStoredFilterRow(jobLabels) || !isStoredFilterRow(locationNames) || !isStoredFilterRow(timeLabels)) {
		// DEFAULT: a hand-edited or half-written value reads as "never saved" — the whole building — rather than
		// narrowing a head nurse's screen to something they never chose. It is logged and removed so it cannot
		// keep doing that silently on every visit.
		console.error(`Discarding a malformed gaps-view filter for department ${departmentId}:`, stored);
		_removeItem(_userIdToUserStorageKey(userId, key));
		return null;
	}
	return { jobLabels, locationNames, timeLabels };
}

const isStoredFilterRow = (row: unknown): row is string[] | null => row === null || (Array.isArray(row) && row.every((value) => typeof value === "string"));

function setGapsViewFilter(departmentId: string, filter: GapsViewFilterPreference, userId: string): void {
	// No legacy (user-less) write, for the same reason as the view preference: the legacy key is shared
	// by every user on the browser, so one supervisor's unit scope would follow the next one onto the
	// same tablet. See `setMobileStaffingView`.
	_setItemPerUserId(userId, `${gapsViewFilterKeyPrefix}${departmentId}`, filter, true);
}

function removeGapsViewFilter(departmentId: string, userId: string): void {
	_removeItem(_userIdToUserStorageKey(userId, `${gapsViewFilterKeyPrefix}${departmentId}`));
}

const scheduleTemplateFilterStorageKey = (filterKey: string, departmentId: string, userId: string) =>
	_userIdToUserStorageKey(userId, `${scheduleTemplateFilterKeyPrefix}${filterKey}_${departmentId}`);

/**
 * Reads only the per-user key — never the legacy user-less fallback of `_getItemPerUserId`. Nothing writes that
 * key for these filters, so whatever sits there belongs to whoever else used this browser.
 */
function readScheduleTemplateFilter<T>(filterKey: string, departmentId: string, userId: string, isValid: (value: unknown) => value is T): T | null {
	const storageKey = scheduleTemplateFilterStorageKey(filterKey, departmentId, userId);
	const stored = _getItem<unknown>(storageKey);
	if (stored !== null && isValid(stored)) return stored;
	// `_getItem` reads unparseable JSON as null too, so only a key that is really absent counts as "never saved".
	if (stored === null && !hasItem(storageKey)) return null;
	// DEFAULT: a hand-edited or half-written value reads as "never saved" — the filter shows everything — rather than
	// narrowing the schedule to something nobody chose. Reported and removed so it doesn't do that on every visit.
	console.error(`Discarding a malformed Schedule Template filter ${filterKey} for department ${departmentId}:`, stored);
	trackErrorEvent("schedule_template_filter_malformed", { filter_key: filterKey, department_id: departmentId });
	_removeItem(storageKey);
	return null;
}

const isStringList = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");
const SHIFT_WINDOW_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const isStoredShiftWindow = (value: unknown): value is StoredShiftWindow =>
	typeof value === "object" &&
	value !== null &&
	"start" in value &&
	"end" in value &&
	typeof value.start === "string" &&
	typeof value.end === "string" &&
	SHIFT_WINDOW_TIME_PATTERN.test(value.start) &&
	SHIFT_WINDOW_TIME_PATTERN.test(value.end);

/** The options a user turned off in one Schedule Template filter, or `null` when they never turned any off. */
function getScheduleTemplateExcludedOptions(filterKey: string, departmentId: string, userId: string): string[] | null {
	return readScheduleTemplateFilter(filterKey, departmentId, userId, isStringList);
}

function setScheduleTemplateExcludedOptions(filterKey: string, departmentId: string, excluded: string[], userId: string): void {
	if (excluded.length === 0) {
		_removeItem(scheduleTemplateFilterStorageKey(filterKey, departmentId, userId));
		return;
	}
	// No legacy (user-less) write: that key is shared by every user on the browser. See `setGapsViewFilter`.
	_setItemPerUserId(userId, `${scheduleTemplateFilterKeyPrefix}${filterKey}_${departmentId}`, excluded, true);
}

/** The time-filter chip a user picked, or `null` for All. */
function getScheduleTemplateShiftWindow(filterKey: string, departmentId: string, userId: string): StoredShiftWindow | null {
	return readScheduleTemplateFilter(filterKey, departmentId, userId, isStoredShiftWindow);
}

function setScheduleTemplateShiftWindow(filterKey: string, departmentId: string, window: StoredShiftWindow | null, userId: string): void {
	if (window === null) {
		_removeItem(scheduleTemplateFilterStorageKey(filterKey, departmentId, userId));
		return;
	}
	_setItemPerUserId(userId, `${scheduleTemplateFilterKeyPrefix}${filterKey}_${departmentId}`, window, true);
}

function removeImpersonationToken(): void {
	_removeItem("impersonation_token");
}

function removeOriginalToken(): void {
	_removeItem("originalToken");
}

function getActivityLogViewMode(): "grouped" | "raw" {
	return _getItem<"grouped" | "raw">(activityLogViewModeKey) || "raw";
}

function setActivityLogViewMode(value: "grouped" | "raw"): void {
	_setItem(activityLogViewModeKey, value);
}

/**
 * LocalStorageService provides a simple interface for managing local storage.
 * Avoid accessing localStorage directly in components - add your logic here instead and export the specific functions.
 */
export const localStorageService = {
	hasItem,
	setChosenDepartmentID,
	getChosenDepartmentID,
	setChosenView,
	getChosenView,
	storeOriginalTokenOnce,
	getOriginalToken,
	removeChosenView,
	removeChosenDepartmentID,
	setDepartmentLookAppliedFor,
	getDepartmentLookAppliedFor,
	removeDepartmentLookAppliedFor,
	setClientGroupedRefreshData,
	removeClientGroupedRefreshData,
	getClientGroupedRefreshData,
	resetClientGroupedRefreshData,
	setSavedCredentials,
	getSavedCredentials,
	removeSavedCredentials,
	setBiometricAvailable,
	getBiometricAvailable,
	removeBiometricAvailable,
	setBiometricType,
	getBiometricType,
	removeBiometricType,
	setDeclineBiometric,
	getDeclineBiometric,
	removeDeclineBiometric,
	clear,
	setRecurringShiftSavedRecurrenceType,
	getRecurringShiftSavedRecurrenceType,
	removeRecurringShiftSavedRecurrenceType,
	setScheduleTemplateTab,
	getScheduleTemplateTab,
	setConsecutivePeriod,
	getConsecutivePeriod,
	saveTableSortingState,
	getTableSortingState,
	removeTableSortingState,
	getOwnTableSortingState,
	saveTableColumnOrder,
	getTableColumnOrder,
	removeTableColumnOrder,
	saveTableColumnVisibility,
	getTableColumnVisibility,
	removeTableColumnVisibility,
	saveTableColumnWidths,
	getTableColumnWidths,
	removeTableColumnWidths,
	setOpsOwnerFilter,
	getOpsOwnerFilter,
	setMultiSizePopupState,
	getMultiSizePopupState,
	getMobilePromotionBannerHide,
	setMobilePromotionBannerHide,
	removeMobilePromotionBannerHide,
	getRevealWage,
	setRevealWage,
	removeRevealWage,
	getActivityLogDevMode,
	setActivityLogDevMode,
	getActivityLogShowPunches,
	setActivityLogShowPunches,
	getLastCtrlZTipTimestamp,
	setLastCtrlZTipTimestamp,
	getCtrlZTipUsed,
	setCtrlZTipUsed,
	resetSwipeJiggleHintLastShown,
	removeSwipeJiggleHintLastShown,
	getSwipeJiggleHintLastShown,
	getFeedbackPopoverClosed,
	setFeedbackPopoverClosed,
	getTutorialDismissed,
	setTutorialDismissed,
	getOneTimeNoticeDismissed,
	setOneTimeNoticeDismissed,
	getPopupClosed,
	setPopupClosed,
	getLastPopupShownDate,
	setLastPopupShownDate,
	getDraftModeState,
	setDraftModeState,
	removeDraftModeState,
	getSectionOrder,
	setSectionOrder,
	removeSectionOrder,
	getCollapsedSections,
	setCollapsedSections,
	getExpandedLocationSections,
	setExpandedLocationSections,
	getViewWeekStartDay,
	setViewWeekStartDay,
	getLeakageReportFilters,
	setLeakageReportFilters,
	getWeeklyGapsContractSections,
	setWeeklyGapsContractSections,
	getDayViewStandardContractSections,
	setDayViewStandardContractSections,
	getDayViewGapsContractSections,
	setDayViewGapsContractSections,
	getMcOtMode,
	setMcOtMode,
	getPickupSuggestionState,
	setPickupSuggestionState,
	removeImpersonationToken,
	removeOriginalToken,
	getDisciplinaryDateSortOrder,
	setDisciplinaryDateSortOrder,
	getWeeklyGapsDaySummaryCollapsed,
	setWeeklyGapsDaySummaryCollapsed,
	getDraggableFabOffset,
	setDraggableFabOffset,
	getActivityLogViewMode,
	setActivityLogViewMode,
	getMobileStaffingView,
	setMobileStaffingView,
	getGapsViewFilter,
	setGapsViewFilter,
	removeGapsViewFilter,
	getScheduleTemplateExcludedOptions,
	setScheduleTemplateExcludedOptions,
	getScheduleTemplateShiftWindow,
	setScheduleTemplateShiftWindow,
};
